import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { setCurrentUser } from './currentUserSlice';
import { addUser, setUsers } from './usersSlice';
import { addInvestor, setInvestors } from './investorsSlice';
import {
  addCompanyInvestor,
  setCompanyInvestors,
} from './companyInvestorsSlice';
import { addCompanyMember, setCompanyMembers } from './companyMembersSlice';
import type { UndecoratedProfile } from '@flemingmetrics/api-client';
import { mapToKey } from '@/utils/arrays';

type ProfilesState = {
  byId: Record<string, UndecoratedProfile>;
  byUserId: Record<string, number>;
};

const initialState: ProfilesState = { byId: {}, byUserId: {} };

export const profilesSlice = createSlice({
  name: 'profiles',
  initialState,
  reducers: {
    addProfile: (state, action: PayloadAction<UndecoratedProfile>) => {
      return getNextState(state, [action.payload]);
    },
    setProfiles: (state, action: PayloadAction<UndecoratedProfile[]>) => {
      return getNextState(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCurrentUser, (state, action) => {
        return getNextState(state, [action.payload.profile]);
      })
      .addCase(setUsers, (state, action) => {
        return getNextState(state, mapToKey(action.payload, 'profile'));
      })
      .addCase(addUser, (state, action) => {
        return getNextState(state, mapToKey([action.payload], 'profile'));
      })
      .addCase(setCompanyMembers, (state, action) => {
        return getNextState(state, mapToKey(action.payload, 'profile'));
      })
      .addCase(addCompanyMember, (state, action) => {
        return getNextState(state, mapToKey([action.payload], 'profile'));
      })
      .addCase(setCompanyInvestors, (state, action) => {
        return getNextState(
          state,
          action.payload.map((ci) => ci.investor.profile)
        );
      })
      .addCase(addCompanyInvestor, (state, action) => {
        return getNextState(state, [action.payload.investor.profile]);
      })
      .addCase(setInvestors, (state, action) => {
        return getNextState(state, mapToKey(action.payload, 'profile'));
      })
      .addCase(addInvestor, (state, action) => {
        return getNextState(state, [action.payload.profile]);
      });
  },
});

function getNextState(state: ProfilesState, profiles: UndecoratedProfile[]) {
  const byId = { ...state.byId };
  const byUserId = { ...state.byUserId };
  profiles.forEach((profile) => {
    byId[profile.id] = profile;
    if (profile.userId) byUserId[profile.userId] = profile.id;
  });
  return { byId, byUserId };
}

export const { addProfile, setProfiles } = profilesSlice.actions;

export default profilesSlice.reducer;
